import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ethers } from "ethers";
import { useWeb3 } from "../context/Web3Context";
import { useUserProfile, useUSDT, useTx } from "../hooks/useContract";
import { recordOfferEvent, useMyRewards } from "../hooks/useOffers";
import { useNotification } from "../hooks/useNotification";
import { formatUSDT, formatUSDTCompact, shortAddress, bytes32ToString, formatTimestamp, timeAgo, stringToBytes32 } from "../utils/format";
import { CONTRACT_ADDRESS } from "../config/contract";
import { PACKAGES, ACTIVITY_TYPES, DAILY_CLUB_NAMES, INCOME_AMOUNT_TO_PACKAGE } from "../config/constants";
import { parseError } from "../utils/errorParser";
import Button from "../components/Common/Button";
import { Card, StatCard } from "../components/Common/Card";
import Modal from "../components/Common/Modal";
import FullScreenLoader from "../components/Loader/FullScreenLoader";
import RingChart from "../components/Charts/RingChart";
import BarChart from "../components/Charts/BarChart";

// Compute the highest rank the user currently holds.
// A "rank" IS a royalty/club — same concept. Royalty N eligibility on the contract:
//   owns pkg (N+1) AND has 2+ active directs in pkg (N+1).
// We derive from criteria (packages + directRefsCountPerPackage) rather than
// trusting the on-chain `dailyClubEligibility` flag, because that flag is
// only updated on activation events and can be stale for migrated users.
// The criteria calculation matches _isEligibleForDaily() in Global888Logic.sol.
function computeHighestRank(packages, directRefsCountPerPackage) {
  const names = ["Starter","Builder","Achiever","Leader","Mentor","Director","Executive"];
  // Iterate from top rank (7) downward — return highest achieved
  for (let clubId = 7; clubId >= 1; clubId--) {
    const requiredPkg = clubId + 1;                              // Royalty N needs pkg N+1
    const pkg = packages?.[requiredPkg - 1];
    if (!pkg?.isActive) continue;
    const directs = directRefsCountPerPackage?.[requiredPkg - 1] || 0;
    if (directs < 2) continue;
    return {
      name: names[clubId - 1],
      tier: `royalty-${clubId}`,
      color: clubId === 7 ? "#ec4899" : clubId >= 5 ? "#a78bfa" : "#00e5ff",
      achieved: true,
      clubId,
    };
  }
  // No rank achieved yet — placeholder so users know the ladder exists
  return {
    name: "No Rank Yet",
    tier: "none",
    color: "#5c6b8a",
    achieved: false,
    clubId: 0,
  };
}

function Home() {
  const navigate = useNavigate();
  const notify = useNotification();
  const { account, isCorrectChain, connect, getContract, getLogic, getUsdt } = useWeb3();
  const { loading, isRegistered, user, packages, incomeBreakdown, claimable, eligibility, directRefsCountPerPackage, reload } = useUserProfile();
  const { balance: usdtBalance, allowance, reload: reloadUsdt } = useUSDT();
  const { send, pending } = useTx();
  // Reward entries for this wallet (PHP-backed). Must be called unconditionally
  // at the top level — hook rules forbid calling after an early return.
  const { rewards: userRewards } = useMyRewards(account);

  const [uplineInfo, setUplineInfo] = useState({ name: "", id: "" });
  const [teamSize, setTeamSize] = useState(0);
  const [showRewardsModal, setShowRewardsModal] = useState(false);
  const [recentActivities, setRecentActivities] = useState([]);
  const [totalActivityCount, setTotalActivityCount] = useState(0);

  const [showAllActivities, setShowAllActivities] = useState(false);
  const [activityPage, setActivityPage] = useState(0);
  const [allActivities, setAllActivities] = useState([]);
  const [loadingAll, setLoadingAll] = useState(false);

  const [showRegForModal, setShowRegForModal] = useState(false);
  const [showDirectsModal, setShowDirectsModal] = useState(false);
  const [directsList, setDirectsList] = useState([]);
  const [directsLoading, setDirectsLoading] = useState(false);
  const [rankHistory, setRankHistory] = useState([]);

  const cancelRef = useRef(false);

  // Fetch upline info + team size + recent activities
  useEffect(() => {
    cancelRef.current = false;
    const load = async () => {
      const contract = getContract();
      if (!contract || !user?.id) return;
      try {
        // Upline
        if (user.referrerId && user.referrerId !== "0") {
          const rawUp = await contract.users(user.referrerId);
          if (!cancelRef.current) {
            setUplineInfo({ id: user.referrerId, name: bytes32ToString(rawUp.name) });
          }
        }
        // Team size from Package 1 matrix (canonical total team)
        try {
          const ts = await contract.packageMatrixTeamSize(1, user.id);
          if (!cancelRef.current) setTeamSize(ts.toNumber ? ts.toNumber() : Number(ts.toString()));
        } catch {
          if (!cancelRef.current) setTeamSize(0);
        }
        // Recent 5 activities
        const res = await contract.getUserActivities(user.id, 0, 5);
        const arr = res.activities || res[0];
        const total = res.totalCount || res[2];
        const list = arr.map((a) => ({
          activityType: a.activityType,
          timestamp: a.timestamp.toNumber ? a.timestamp.toNumber() : Number(a.timestamp.toString()),
          relatedId: a.relatedId.toString(),
          amount: a.amount,
          netAmount: a.netAmount,
        }));
        if (!cancelRef.current) {
          setRecentActivities(list);
          setTotalActivityCount(total.toNumber ? total.toNumber() : Number(total.toString()));
        }

        // (Income → package lookup is now static/deterministic — see INCOME_AMOUNT_TO_PACKAGE
        // in constants.js. No event query needed.)

        // Rank achievement history — only 7 daily royalties now
        try {
          const dailyElig = await contract.queryFilter(
            contract.filters.DailyEligibilityUpdated(user.id), 0, "latest"
          );
          const earned = [];
          const seen = new Set();
          for (const e of dailyElig) {
            if (e.args.eligible) {
              const c = e.args.clubId.toNumber();
              const key = `d${c}`;
              if (!seen.has(key)) { seen.add(key); earned.push({ kind: "daily", club: c, block: e.blockNumber }); }
            }
          }
          const provider = contract.provider || contract.signer?.provider;
          if (provider) {
            const uniqueBlocks = [...new Set(earned.map((x) => x.block))];
            const blockMap = {};
            await Promise.all(uniqueBlocks.map(async (bn) => {
              try { const b = await provider.getBlock(bn); blockMap[bn] = b?.timestamp || 0; } catch {}
            }));
            for (const e of earned) e.timestamp = blockMap[e.block] || 0;
          }
          earned.sort((a, b) => a.block - b.block);
          if (!cancelRef.current) setRankHistory(earned);
        } catch { /* non-fatal */ }
      } catch {
        // ignore
      }
    };
    load();
    return () => { cancelRef.current = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, packages?.length]);

  // Fetch direct referral details when modal opens
  useEffect(() => {
    if (!showDirectsModal) return;
    const contract = getContract();
    if (!contract || !user?.id) return;
    let cancelled = false;
    const load = async () => {
      setDirectsLoading(true);
      try {
        const ids = await contract.getUserReferrals(user.id);
        const idList = ids.map((x) => x.toString());
        const details = await Promise.all(
          idList.map(async (id) => {
            try {
              const u = await contract.users(id);
              // Find highest active package AND compute rank from criteria
              // (matches _isEligibleForDaily contract logic: own P(N+1) + 2+ directs in P(N+1))
              let highest = 0;
              const activePackages = new Array(9).fill(false); // index by pkg id 1..8
              for (let p = 1; p <= 8; p++) {
                try {
                  const raw = await contract.userPackages(id, p);
                  if (raw.isActive) {
                    activePackages[p] = true;
                    if (p > highest) highest = p;
                  }
                } catch {}
              }
              // Compute rank from criteria — highest royalty where user owns the required pkg
              // AND has 2+ directs in that pkg. Only check ranks for which they have a package.
              let rankName = null;
              let rankColor = null;
              const rankNames = ["Starter","Builder","Achiever","Leader","Mentor","Director","Executive"];
              for (let c = 7; c >= 1; c--) {
                const reqPkg = c + 1;
                if (!activePackages[reqPkg]) continue;
                try {
                  const directCount = await contract.activeDirectsPerPackage(id, reqPkg);
                  const dcNum = directCount.toNumber ? directCount.toNumber() : Number(directCount.toString());
                  if (dcNum >= 2) {
                    rankName = rankNames[c - 1];
                    rankColor = c === 7 ? "#ec4899" : c >= 5 ? "#a78bfa" : "#00e5ff";
                    break;
                  }
                } catch {}
              }
              return {
                id,
                name: bytes32ToString(u.name),
                wallet: u.wallet,
                highest,
                rankName,
                rankColor,
                isBlocked: u.isBlocked,
              };
            } catch {
              return null;
            }
          })
        );
        if (!cancelled) setDirectsList(details.filter(Boolean));
      } catch {
        // ignore
      } finally {
        if (!cancelled) setDirectsLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDirectsModal, user?.id]);

  // When user opens "View More" modal, fetch up to 100 activities (contract cap)
  useEffect(() => {
    if (!showAllActivities) return;
    const contract = getContract();
    if (!contract || !user?.id) return;
    let cancelled = false;
    const load = async () => {
      setLoadingAll(true);
      try {
        const res = await contract.getUserActivities(user.id, 0, 100);
        const arr = res.activities || res[0];
        const list = arr.map((a) => ({
          activityType: a.activityType,
          timestamp: a.timestamp.toNumber ? a.timestamp.toNumber() : Number(a.timestamp.toString()),
          relatedId: a.relatedId.toString(),
          amount: a.amount,
          netAmount: a.netAmount,
        }));
        if (!cancelled) setAllActivities(list);
      } catch {
        // ignore
      } finally {
        if (!cancelled) setLoadingAll(false);
      }
    };
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAllActivities, user?.id]);

  if (!account) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">◇</div>
        <div className="empty-state-text mb-lg">Connect wallet to continue.</div>
        <Button variant="primary" onClick={connect}>Connect Wallet</Button>
      </div>
    );
  }

  if (loading) return <FullScreenLoader text="Loading Profile" />;

  if (isCorrectChain && !isRegistered) {
    return (
      <div className="empty-state animate-fade-in-up">
        <div className="empty-state-icon">◇</div>
        <div className="empty-state-text mb-lg">You're not registered yet.</div>
        <Button variant="primary" onClick={() => navigate("/register")}>Register Now</Button>
      </div>
    );
  }

  // Highest active package (used as rank badge)
  const highestPkg = [...packages].reverse().find((p) => p.isActive);
  const userRank = computeHighestRank(packages, directRefsCountPerPackage);
  const activeCount = packages.filter((p) => p.isActive).length;
  const directs = parseInt(user?.totalDirectReferrals || "0", 10);

  // Claim all handler — no more claimAll() on-chain; loop each non-zero royalty
  const claimAll = async () => {
    const logic = getLogic();
    if (!logic) return notify.error("Logic contract not configured");
    if (!claimable || claimable.total.isZero()) {
      notify.warning("Nothing to claim");
      return;
    }
    let claimedCount = 0;
    for (let c = 1; c <= 7; c++) {
      const amt = claimable.daily?.[c - 1];
      if (!amt || amt.isZero()) continue;
      const ok = await send(() => logic.claimDailyClub(c), {
        pendingMsg: `Claiming royalty ${c}...`,
        successMsg: `Royalty ${c} claimed`,
      });
      if (ok) claimedCount++;
    }
    if (claimedCount > 0) reload();
  };

  const refLink =
    typeof window !== "undefined"
      ? `${window.location.origin}/register?ref=${user?.id || ""}`
      : "";

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(refLink);
      notify.success("Link copied");
    } catch {
      notify.error("Could not copy — please copy manually");
    }
  };

  // Income breakdown as bars — new contract has only 3 streams
  const bars = incomeBreakdown
    ? [
        { label: "Direct",  value: parseFloat(ethers.utils.formatUnits(incomeBreakdown.directReferralIncome, 18)), color: "#00e5ff" },
        { label: "Matrix",  value: parseFloat(ethers.utils.formatUnits(incomeBreakdown.matrixLevelIncome, 18)), color: "#8b5cf6" },
        { label: "Royalty", value: parseFloat(ethers.utils.formatUnits(incomeBreakdown.dailyClubIncome, 18)), color: "#ec4899" },
      ]
    : [];

  // Directs vs team ring
  const teamOnly = Math.max(0, teamSize - directs);
  const ringSegments = [
    { value: directs, color: "#00e5ff", label: "Direct" },
    { value: teamOnly, color: "#8b5cf6", label: "Team" },
  ];

  return (
    <div>
      {/* USER HEADER */}
      <div className="user-header mb-lg">
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div className="user-avatar">
            {user?.name ? user.name[0]?.toUpperCase() : "◇"}
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 6, flexWrap: "wrap" }}>
              <span style={{ fontFamily: "var(--font-display)", fontSize: 16, fontWeight: 700 }}>
                {user?.name || "Unnamed"}
              </span>
              {highestPkg && (
                <div className="pkg-pill">
                  <span className="pkg-pill-tier">P{highestPkg.id}</span>
                  <span className="pkg-pill-price">${highestPkg.price}</span>
                </div>
              )}
              {userRewards.length > 0 && (
                <button
                  type="button"
                  onClick={() => setShowRewardsModal(true)}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 4,
                    padding: "3px 8px",
                    borderRadius: 999,
                    background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
                    color: "#1a1407",
                    border: "none",
                    fontFamily: "var(--font-display)",
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: "0.04em",
                    cursor: "pointer",
                    boxShadow: "0 0 10px rgba(251,191,36,0.5)",
                    textTransform: "uppercase",
                  }}
                  title="View your rewards"
                >
                  🏆 {userRewards.length} Reward{userRewards.length !== 1 ? "s" : ""}
                </button>
              )}
            </div>
            <div style={{ fontSize: 12, color: "var(--text-muted)", fontFamily: "var(--font-mono)" }}>
              ID #{user?.id} · {shortAddress(account, 4)}
            </div>
          </div>
        </div>
        {/* Rank strip — full-width, premium look */}
        {userRank && (
          <div style={{
            marginTop: 14,
            padding: "10px 14px",
            borderRadius: 12,
            background: `linear-gradient(90deg, ${userRank.color}22 0%, ${userRank.color}11 60%, transparent 100%)`,
            border: `1px solid ${userRank.color}66`,
            display: "flex",
            alignItems: "center",
            gap: 12,
            position: "relative",
            overflow: "hidden",
            boxShadow: `0 0 20px ${userRank.color}22, inset 0 0 20px ${userRank.color}0f`,
          }}>
            {/* Star medallion */}
            <div style={{
              width: 40, height: 40, flexShrink: 0,
              borderRadius: "50%",
              background: `radial-gradient(circle at 30% 30%, ${userRank.color}, ${userRank.color}88)`,
              border: `2px solid ${userRank.color}`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 20, color: "#000",
              boxShadow: `0 0 14px ${userRank.color}aa`,
            }}>
              ★
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{
                fontSize: 10, fontWeight: 600,
                color: `${userRank.color}`, opacity: 0.85,
                letterSpacing: "0.15em",
                textTransform: "uppercase",
                marginBottom: 2,
              }}>
                Current Rank
              </div>
              <div style={{
                fontFamily: "'Sora', 'Inter', sans-serif",
                fontSize: 22,
                fontWeight: 700,
                color: "var(--text-primary)",
                letterSpacing: "-0.01em",
                lineHeight: 1,
              }}>
                {userRank.name}
              </div>
            </div>
            {/* Tier badge on right */}
            <div style={{
              padding: "4px 10px",
              borderRadius: 6,
              background: userRank.color,
              color: "#000",
              fontFamily: "var(--font-display)",
              fontWeight: 700,
              fontSize: 10,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              flexShrink: 0,
            }}>
              {userRank.achieved ? "Royalty" : "—"}
            </div>
          </div>
        )}

        <div style={{ marginTop: 12, paddingTop: 12, borderTop: "1px solid var(--border)", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
          <div>
            <div className="stat-card-label" style={{ marginBottom: 3 }}>Upline</div>
            <div style={{ fontSize: 12, color: "var(--text-primary)" }}>
              {uplineInfo.name || "—"} <span style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>#{uplineInfo.id}</span>
            </div>
          </div>
          <div>
            <div className="stat-card-label" style={{ marginBottom: 3 }}>Joined</div>
            <div style={{ fontSize: 12, color: "var(--text-primary)" }}>
              {user ? formatTimestamp(user.joinTime) : "—"}
            </div>
          </div>
        </div>
      </div>

      {/* REFERRAL LINK */}
      <div
        style={{
          background: "#0a0e24",
          border: "1px dashed #00e5ff",
          borderRadius: 10,
          padding: "10px 12px",
          display: "flex",
          alignItems: "center",
          gap: 8,
          marginBottom: 16,
        }}
      >
        <input
          type="text"
          readOnly
          value={refLink}
          onFocus={(e) => e.target.select()}
          style={{
            flex: 1,
            minWidth: 0,
            background: "transparent",
            border: "none",
            outline: "none",
            color: "#00e5ff",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 12,
            fontWeight: 500,
            padding: "4px 0",
            width: "100%",
          }}
        />
        <button
          type="button"
          onClick={copyLink}
          style={{
            background: "#00e5ff",
            color: "#000",
            border: "none",
            padding: "6px 14px",
            borderRadius: 6,
            fontSize: 12,
            fontWeight: 700,
            cursor: "pointer",
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          Copy
        </button>
      </div>

      {/* TOTAL INCOME — BIG */}
      <div className="card card-glow mb-lg" style={{ padding: 20, textAlign: "center" }}>
        <div className="stat-card-label">Total Income</div>
        <div
          style={{
            fontFamily: "'Sora', 'Inter', sans-serif",
            fontSize: 46,
            fontWeight: 800,
            background: "var(--grad-cyber)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            lineHeight: 1.1,
            margin: "8px 0",
            letterSpacing: "-0.03em",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          ${user ? formatUSDT(user.totalIncome) : "0.00"}
        </div>
        <div className="stat-card-sub">Lifetime earnings · USDT</div>
      </div>

      {/* TOTAL CLAIMABLE */}
      <div className="card mb-lg">
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
          <div>
            <div className="stat-card-label">Total Claimable</div>
            <div style={{ fontFamily: "'Sora', 'Inter', sans-serif", fontSize: 28, fontWeight: 700, color: "var(--cyan)", lineHeight: 1.1, letterSpacing: "-0.02em", fontVariantNumeric: "tabular-nums" }}>
              ${claimable ? formatUSDT(claimable.total) : "0.00"}
            </div>
          </div>
          <Button
            variant="primary"
            onClick={claimAll}
            loading={pending}
            disabled={!claimable || claimable.total.isZero()}
          >
            Claim All
          </Button>
        </div>
      </div>

      {/* NETWORK — Ring chart */}
      <div className="section-header"><div className="section-title">Network</div></div>
      <div className="card mb-lg">
        <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
          <RingChart
            segments={ringSegments.every((s) => s.value === 0) ? [{ value: 1, color: "rgba(139,155,209,0.2)" }] : ringSegments}
            size={130}
            thickness={14}
            center={teamSize}
            subCenter="Team"
          />
          <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 10 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3, justifyContent: "space-between" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <span style={{ width: 8, height: 8, background: "var(--cyan)", borderRadius: 2, boxShadow: "0 0 4px var(--cyan)" }} />
                  <span style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Direct</span>
                </div>
                {directs > 0 && (
                  <button
                    onClick={() => setShowDirectsModal(true)}
                    style={{
                      background: "transparent",
                      border: "1px solid var(--cyan-border)",
                      color: "var(--cyan)",
                      padding: "2px 8px",
                      borderRadius: 4,
                      fontSize: 10,
                      fontWeight: 600,
                      cursor: "pointer",
                      letterSpacing: "0.05em",
                    }}
                  >
                    VIEW
                  </button>
                )}
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, color: "var(--text-primary)" }}>{directs}</div>
            </div>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 3 }}>
                <span style={{ width: 8, height: 8, background: "var(--violet)", borderRadius: 2, boxShadow: "0 0 4px var(--violet)" }} />
                <span style={{ fontSize: 11, color: "var(--text-muted)", textTransform: "uppercase", letterSpacing: "0.05em" }}>Team</span>
              </div>
              <div style={{ fontFamily: "var(--font-display)", fontSize: 24, fontWeight: 700, color: "var(--text-primary)" }}>{teamSize}</div>
            </div>
          </div>
        </div>
      </div>

      {/* INCOME BREAKDOWN — Bar chart */}
      <div className="section-header"><div className="section-title">Income Breakdown</div></div>
      <div className="card mb-lg">
        <BarChart bars={bars} height={160} />
        <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 16, paddingTop: 14, borderTop: "1px solid var(--border)" }}>
          {bars.map((b, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ width: 10, height: 10, background: b.color, borderRadius: 3, boxShadow: `0 0 6px ${b.color}`, flexShrink: 0 }} />
              <span style={{ fontSize: 14, color: "var(--text-secondary)", flex: 1, fontWeight: 500 }}>{b.label}</span>
              <span style={{
                fontFamily: "'Sora', 'Inter', sans-serif",
                fontSize: 16,
                fontWeight: 700,
                color: "var(--text-primary)",
                fontVariantNumeric: "tabular-nums",
                letterSpacing: "-0.02em",
              }}>
                ${b.value.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* REGISTER FOR DOWNLINE / ACTIVATE FOR USER */}
      <div className="section-header"><div className="section-title">Grow Your Network</div></div>
      <div className="card mb-lg">
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: "var(--text-primary)" }}>Register or Activate</div>
            <div className="stat-card-sub" style={{ marginTop: 4 }}>Register a new user under any sponsor, or activate a package for an existing user</div>
          </div>
          <Button variant="secondary" size="sm" onClick={() => setShowRegForModal(true)}>+ Add</Button>
        </div>
      </div>

      {/* RANK ACHIEVEMENTS */}
      {rankHistory.length > 0 && (
        <>
          <div className="section-header"><div className="section-title">Rank Achievements</div></div>
          <div className="card mb-lg">
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {rankHistory.map((r, i) => {
                let name, color;
                name = ["Starter","Builder","Achiever","Leader","Mentor","Director","Executive"][r.club - 1];
                color = r.club === 7 ? "#ec4899" : r.club >= 5 ? "#a78bfa" : "#00e5ff";
                return (
                  <div key={i} style={{
                    display: "flex", alignItems: "center", gap: 10,
                    padding: "8px 10px",
                    background: "#050815",
                    border: `1px solid ${color}33`,
                    borderRadius: 8,
                  }}>
                    <div style={{
                      width: 30, height: 30, borderRadius: "50%",
                      background: `${color}22`,
                      border: `1.5px solid ${color}`,
                      color, display: "flex", alignItems: "center", justifyContent: "center",
                      fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 14,
                      flexShrink: 0,
                    }}>
                      ★
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 13, color }}>
                        {name}
                      </div>
                      <div style={{ fontSize: 10, color: "var(--text-muted)" }}>
                        Achieved
                      </div>
                    </div>
                    <div style={{ fontSize: 10, color: "var(--text-muted)", textAlign: "right", fontFamily: "var(--font-mono)" }}>
                      {r.timestamp ? formatTimestamp(r.timestamp) : `Block ${r.block}`}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* RECENT HISTORY */}
      <div className="section-header">
        <div className="section-title">Recent History</div>
        <span className="badge badge-cyan mono">{totalActivityCount}</span>
      </div>

      {recentActivities.length === 0 ? (
        <div className="empty-state" style={{ padding: 16 }}>
          <div className="empty-state-text">No history yet</div>
        </div>
      ) : (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {recentActivities.map((a, i) => (
              <ActivityRow key={i} a={a} />
            ))}
          </div>
          {totalActivityCount > 5 && (
            <Button variant="ghost" full style={{ marginTop: 12 }} onClick={() => { setActivityPage(0); setShowAllActivities(true); }}>
              View More
            </Button>
          )}
        </>
      )}

      {/* ============ MODAL: All History (paginated) ============ */}
      <Modal open={showAllActivities} onClose={() => setShowAllActivities(false)} title="All History">
        {loadingAll ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <span className="spinner spinner-lg" />
            <div style={{ marginTop: 12, color: "var(--text-muted)" }}>Loading...</div>
          </div>
        ) : (
          <ActivityPaginated activities={allActivities} page={activityPage} setPage={setActivityPage} perPage={10} />
        )}
      </Modal>

      {/* ============ MODAL: Register / Activate for others ============ */}
      <Modal open={showRegForModal} onClose={() => setShowRegForModal(false)} title="Register / Activate">
        <RegisterForForm
          myId={user?.id || ""}
          usdtBalance={usdtBalance}
          allowance={allowance}
          onDone={() => { setShowRegForModal(false); reload(); reloadUsdt(); }}
        />
      </Modal>

      {/* ============ MODAL: Directs list ============ */}
      <Modal open={showDirectsModal} onClose={() => setShowDirectsModal(false)} title={`Your Directs (${directs})`}>
        {directsLoading ? (
          <div style={{ textAlign: "center", padding: 24 }}>
            <span className="spinner spinner-lg" />
            <div style={{ marginTop: 12, color: "var(--text-muted)" }}>Loading directs...</div>
          </div>
        ) : directsList.length === 0 ? (
          <div className="empty-state-text">No direct referrals yet</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {directsList.map((d) => (
              <DirectRow key={d.id} d={d} />
            ))}
          </div>
        )}
      </Modal>

      {/* Rewards received modal — only shown when user has 1+ reward entries */}
      <Modal open={showRewardsModal} onClose={() => setShowRewardsModal(false)} title={`Your Rewards (${userRewards.length})`}>
        {userRewards.length === 0 ? (
          <div className="empty-state-text">No rewards yet</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {userRewards.map((r, i) => (
              <div
                key={i}
                style={{
                  background: "rgba(251, 191, 36, 0.06)",
                  border: "1px solid rgba(251, 191, 36, 0.3)",
                  borderRadius: 12,
                  padding: 12,
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: "50%",
                    background: "linear-gradient(135deg, #fbbf24, #f59e0b)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    fontSize: 18,
                    flexShrink: 0,
                  }}>🏆</div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700, color: "var(--text-primary)" }}>
                      {r.offerName || "Reward Offer"}
                    </div>
                    <div style={{ fontSize: 11, color: "#fbbf24", fontWeight: 600 }}>
                      {r.rewardLabel || "Bonus"}
                      {r.amount ? ` · ${r.amount}` : ""}
                    </div>
                  </div>
                </div>
                <div style={{ fontSize: 10, color: "var(--text-muted)", display: "flex", justifyContent: "space-between", gap: 8, flexWrap: "wrap" }}>
                  <span>{r.paidAt ? new Date(r.paidAt * 1000).toLocaleString("en-US", { timeZone: "UTC" }) + " UTC" : ""}</span>
                  {r.paidTxHash && (
                    <a
                      href={`https://bscscan.com/tx/${r.paidTxHash}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}
                    >
                      {r.paidTxHash.slice(0, 8)}…
                    </a>
                  )}
                </div>
                {r.note && (
                  <div style={{ marginTop: 6, fontSize: 11, color: "var(--text-secondary)", fontStyle: "italic" }}>
                    "{r.note}"
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

function DirectRow({ d }) {
  const pkgLabel = d.highest > 0 ? PACKAGES[d.highest - 1] : null;
  return (
    <div style={{
      display: "flex", alignItems: "center", gap: 10,
      padding: 10,
      background: "#050815",
      border: "1px solid var(--border-strong)",
      borderRadius: 10,
    }}>
      <div style={{
        width: 36, height: 36, borderRadius: "50%",
        background: "linear-gradient(135deg, var(--cyan), var(--violet))",
        color: "white", fontFamily: "var(--font-display)", fontSize: 14, fontWeight: 700,
        display: "flex", alignItems: "center", justifyContent: "center",
        border: "1px solid var(--cyan)",
        flexShrink: 0,
      }}>
        {d.name ? d.name[0]?.toUpperCase() : "◇"}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {d.name || "—"}
          </span>
          {d.isBlocked && <span className="badge badge-red" style={{ fontSize: 9 }}>BLOCKED</span>}
        </div>
        <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--cyan)", marginTop: 2 }}>
          #{d.id}
        </div>
      </div>
      <div style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 4, flexShrink: 0 }}>
        {pkgLabel ? (
          <span className="badge" style={{ fontSize: 10, padding: "2px 8px", background: "var(--cyan)", color: "#000", border: "none", fontWeight: 700 }}>
            P{pkgLabel.id} · ${pkgLabel.price}
          </span>
        ) : (
          <span className="badge" style={{ fontSize: 10, background: "rgba(139,155,209,0.08)", color: "var(--text-muted)", border: "1px solid var(--border)" }}>
            NO PKG
          </span>
        )}
        {d.rankName ? (
          <span
            className="badge"
            style={{
              fontSize: 9,
              padding: "2px 8px",
              background: d.rankColor || "var(--cyan)",
              color: "#000",
              border: "none",
              fontWeight: 700,
              boxShadow: `0 0 6px ${d.rankColor || "var(--cyan)"}55`,
            }}
          >
            ★ {d.rankName}
          </span>
        ) : (
          <span style={{ fontSize: 9, color: "var(--text-dim)", letterSpacing: "0.03em" }}>NO RANK</span>
        )}
      </div>
    </div>
  );
}

/* ---------------- Sub components ---------------- */

function ActivityRow({ a }) {
  const meta = ACTIVITY_TYPES[a.activityType] || { label: "Activity", icon: "•", color: "var(--text-muted)" };
  let label = meta.label;
  if (a.activityType === 6 && DAILY_CLUB_NAMES[a.relatedId]) {
    label = `${DAILY_CLUB_NAMES[a.relatedId]} Claimed`;
  }
  // Activity types where relatedId is a packageId
  const packageTypes = new Set([2, 3, 9, 10]);
  // Activity types where relatedId is a userId (from/referrer/etc)
  const userIdTypes = new Set([1, 4, 5]);

  const showAmount = a.amount && !a.amount.isZero();

  // Determine the source package for every income-related activity
  let sourcePkg = null;
  if (a.activityType === 4 || a.activityType === 5) {
    // Direct / Matrix income — amounts are deterministic per package
    // (direct = 45% of price, matrix per-level = 5% of price)
    if (a.amount) {
      sourcePkg = INCOME_AMOUNT_TO_PACKAGE[a.amount.toString()] || null;
    }
  } else if (a.activityType === 6) {
    // Daily rank N → source Package (N+1)
    const clubId = Number(a.relatedId || 0);
    if (clubId >= 1 && clubId <= 7) sourcePkg = String(clubId + 1);
  }
  // (Weekly / monthly claim types removed in new contract)

  let subMeta = "";
  if (userIdTypes.has(a.activityType) && a.relatedId && a.relatedId !== "0" && a.activityType !== 1) {
    subMeta = ` · from #${a.relatedId}`;
  } else if (a.activityType === 5) {
    const lvl = a.netAmount && a.netAmount.toNumber ? a.netAmount.toNumber() : Number(a.netAmount || 0);
    if (lvl > 0) subMeta = ` · L${lvl} · from #${a.relatedId}`;
  }

  const pkgBadgeText = packageTypes.has(a.activityType) && a.relatedId && a.relatedId !== "0"
    ? `P${a.relatedId}`
    : sourcePkg
    ? `P${sourcePkg}`
    : null;

  return (
    <div className="list-item">
      <div className="list-item-icon">{meta.icon}</div>
      <div className="list-item-body">
        <div style={{ display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
          <div className="list-item-title" style={{ color: "var(--text-primary)" }}>{label}</div>
          {pkgBadgeText && (
            <span
              className="badge"
              style={{
                fontSize: 9,
                padding: "2px 6px",
                background: "var(--cyan)",
                color: "#000",
                border: "none",
                fontWeight: 700,
                letterSpacing: "0.05em",
              }}
              title="Source package"
            >
              {pkgBadgeText}
            </span>
          )}
        </div>
        <div className="list-item-sub">
          {timeAgo(a.timestamp)}
          {subMeta}
        </div>
      </div>
      {showAmount ? (
        <div className="list-item-value mono">${formatUSDT(a.amount)}</div>
      ) : (
        <div className="list-item-value" style={{ color: "var(--text-dim)" }}>—</div>
      )}
    </div>
  );
}

function ActivityPaginated({ activities, page, setPage, perPage }) {
  const total = activities.length;
  const totalPages = Math.max(1, Math.ceil(total / perPage));
  const start = page * perPage;
  const slice = activities.slice(start, start + perPage);

  return (
    <>
      <div style={{ display: "flex", flexDirection: "column", gap: 6, marginBottom: 12 }}>
        {slice.length === 0 && <div className="empty-state-text">No history yet</div>}
        {slice.map((a, i) => <ActivityRow key={i} a={a} />)}
      </div>
      <div className="pagination">
        <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={page === 0}>‹ Prev</Button>
        <span className="pagination-info">Page {page + 1} / {totalPages}</span>
        <Button variant="ghost" size="sm" onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))} disabled={page >= totalPages - 1}>Next ›</Button>
      </div>
    </>
  );
}

function RegisterForForm({ myId, usdtBalance, allowance, onDone }) {
  const [mode, setMode] = useState("register"); // "register" | "activate"

  return (
    <div>
      {/* Mode tabs */}
      <div className="tabs" style={{ marginBottom: 14 }}>
        <button
          className={`tab ${mode === "register" ? "active" : ""}`}
          onClick={() => setMode("register")}
        >
          Register New
        </button>
        <button
          className={`tab ${mode === "activate" ? "active" : ""}`}
          onClick={() => setMode("activate")}
        >
          Activate Package
        </button>
      </div>

      {mode === "register" ? (
        <RegisterNewForm myId={myId} usdtBalance={usdtBalance} allowance={allowance} onDone={onDone} />
      ) : (
        <ActivateForForm usdtBalance={usdtBalance} allowance={allowance} onDone={onDone} />
      )}
    </div>
  );
}

/* -------- Register a brand-new user -------- */
function RegisterNewForm({ myId, usdtBalance, allowance, onDone }) {
  const notify = useNotification();
  const { getContract, getLogic, getUsdt } = useWeb3();
  const [wallet, setWallet] = useState("");
  const [name, setName] = useState("");
  const [sponsorId, setSponsorId] = useState(String(myId || ""));
  const [sponsorName, setSponsorName] = useState("");
  const [sponsorLookupState, setSponsorLookupState] = useState("idle"); // "idle" | "loading" | "found" | "notfound"
  const [busy, setBusy] = useState(false);

  // Registration always costs Package 1 price. To activate more packages, use the Activate tab.
  const cost = PACKAGES[0].price;
  const costBn = ethers.utils.parseUnits(String(cost), 18);
  const hasBalance = usdtBalance.gte(costBn);

  // Lookup sponsor name when sponsorId changes (debounced)
  useEffect(() => {
    const contract = getContract();
    if (!contract) return;
    const idStr = String(sponsorId || "").trim();
    if (!idStr || isNaN(Number(idStr)) || Number(idStr) <= 0) {
      setSponsorName("");
      setSponsorLookupState("idle");
      return;
    }
    let cancelled = false;
    setSponsorLookupState("loading");
    const t = setTimeout(async () => {
      try {
        const u = await contract.users(idStr);
        if (cancelled) return;
        const zero = "0x0000000000000000000000000000000000000000";
        if (!u.wallet || u.wallet.toLowerCase() === zero) {
          setSponsorName("");
          setSponsorLookupState("notfound");
        } else {
          setSponsorName(bytes32ToString(u.name));
          setSponsorLookupState("found");
        }
      } catch {
        if (!cancelled) {
          setSponsorName("");
          setSponsorLookupState("notfound");
        }
      }
    }, 350);
    return () => { cancelled = true; clearTimeout(t); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sponsorId]);

  const doRegister = async () => {
    if (!ethers.utils.isAddress(wallet)) return notify.error("Invalid wallet address");
    if (!name.trim()) return notify.error("Name is required");
    if (!sponsorId || isNaN(Number(sponsorId))) return notify.error("Invalid sponsor ID");
    if (!hasBalance) return notify.error("Not enough USDT for Package 1");
    const logic = getLogic();
    const usdt = getUsdt();
    if (!logic || !usdt) return notify.error("Logic contract not configured");
    setBusy(true);
    try {
      if (allowance.lt(costBn)) {
        notify.info("Approving USDT...");
        const ax = await usdt.approve(CONTRACT_ADDRESS, ethers.constants.MaxUint256);
        await ax.wait();
        notify.success("Approved");
      }
      let nameBytes;
      try { nameBytes = stringToBytes32(name); }
      catch (nameE) { notify.error(nameE.message); setBusy(false); return; }
      notify.info("Registering...");
      const tx = await logic.registerFor(wallet, sponsorId, nameBytes);
      const receipt = await tx.wait();
      notify.success("Downline registered with Package 1!");
      // Record for active reward offers — tag against the TARGET wallet (new user)
      recordOfferEvent({
        wallet,
        name: name.trim(),
        action: "register",
        packageId: 1,
        amount: "8",
        txHash: receipt?.transactionHash || tx?.hash || "",
      });
      onDone();
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ padding: 10, background: "#050815", border: "1px solid var(--border-strong)", borderRadius: 8, marginBottom: 14, fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
        Registers a new user under any sponsor with <b style={{ color: "var(--cyan)" }}>Package 1 only</b>. Use the <b>Activate Package</b> tab to add higher packages afterward.
      </div>

      <div className="input-group">
        <label className="input-label">New User Wallet</label>
        <input className="input" placeholder="0x..." value={wallet} onChange={(e) => setWallet(e.target.value)} disabled={busy} />
      </div>
      <div className="input-group">
        <label className="input-label">Name</label>
        <input className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={31} disabled={busy} />
      </div>
      <div className="input-group">
        <label className="input-label">Sponsor ID</label>
        <input
          type="number"
          className="input"
          placeholder="e.g. 100000"
          value={sponsorId}
          onChange={(e) => setSponsorId(e.target.value)}
          disabled={busy}
        />
        {/* Sponsor name preview */}
        <div style={{ marginTop: 6, minHeight: 18, fontSize: 12, display: "flex", alignItems: "center", gap: 6 }}>
          {sponsorLookupState === "loading" && (
            <span style={{ color: "var(--text-muted)" }}>Looking up sponsor…</span>
          )}
          {sponsorLookupState === "found" && (
            <>
              <span style={{ color: "var(--text-muted)" }}>Sponsor:</span>
              <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>
                {sponsorName || "Unnamed"}
              </span>
              <span style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>
                #{sponsorId}
              </span>
            </>
          )}
          {sponsorLookupState === "notfound" && (
            <span style={{ color: "var(--error)" }}>Sponsor ID not found</span>
          )}
        </div>
        <div style={{ fontSize: 10, color: "var(--text-muted)", marginTop: 4 }}>
          Defaults to your ID. Change to place the user under a different sponsor.
        </div>
      </div>

      <div className="stat-grid mb-lg">
        <div className="stat-card">
          <div className="stat-card-label">Cost</div>
          <div className="stat-card-value" style={{ color: "var(--cyan)" }}>${cost}</div>
          <div className="stat-card-sub">Package 1</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Your Balance</div>
          <div className="stat-card-value" style={{ color: hasBalance ? "var(--success)" : "var(--error)" }}>
            ${formatUSDT(usdtBalance)}
          </div>
        </div>
      </div>

      <Button variant="primary" full loading={busy} disabled={!hasBalance || !wallet || !name.trim() || !sponsorId || sponsorLookupState === "notfound" || sponsorLookupState === "loading"} onClick={doRegister}>
        Register with Package 1
      </Button>
    </div>
  );
}

/* -------- Activate a single package for an already-registered user -------- */
function ActivateForForm({ usdtBalance, allowance, onDone }) {
  const notify = useNotification();
  const { getContract, getLogic, getUsdt } = useWeb3();
  const [wallet, setWallet] = useState("");
  const [packageId, setPackageId] = useState(2);
  const [checking, setChecking] = useState(false);
  const [targetInfo, setTargetInfo] = useState(null); // { id, name, packages: [bool*8] }
  const [busy, setBusy] = useState(false);

  const cost = PACKAGES[packageId - 1].price;
  const costBn = ethers.utils.parseUnits(String(cost), 18);
  const hasBalance = usdtBalance.gte(costBn);

  // Lookup helper — user pastes wallet, we fetch their user + package status
  const doLookup = async () => {
    if (!ethers.utils.isAddress(wallet)) return notify.error("Invalid wallet address");
    const contract = getContract();
    if (!contract) return;
    setChecking(true);
    setTargetInfo(null);
    try {
      const uid = await contract.addressToId(wallet);
      const uidStr = uid.toString();
      if (uidStr === "0") {
        notify.error("This wallet is not registered yet");
        return;
      }
      const u = await contract.users(uidStr);
      const pkgs = await Promise.all(
        PACKAGES.map(async (p) => {
          try {
            const raw = await contract.userPackages(uidStr, p.id);
            return raw.isActive;
          } catch { return false; }
        })
      );
      // Fetch this user's sponsor (referrer) name + id
      let sponsorId = "";
      let sponsorName = "";
      try {
        const refId = u.referrerId ? u.referrerId.toString() : "0";
        if (refId && refId !== "0") {
          sponsorId = refId;
          const su = await contract.users(refId);
          sponsorName = bytes32ToString(su.name);
        }
      } catch { /* non-fatal */ }
      setTargetInfo({
        id: uidStr,
        name: bytes32ToString(u.name),
        packages: pkgs,
        sponsorId,
        sponsorName,
      });
      // Auto-select the next un-activated package
      const nextIdx = pkgs.findIndex((a) => !a);
      if (nextIdx >= 0) setPackageId(nextIdx + 1);
    } catch (e) {
      notify.error("Lookup failed: " + (e.message?.slice(0, 60) || "unknown"));
    } finally {
      setChecking(false);
    }
  };

  // Only "next un-activated" is enabled (contract enforces sequential activation)
  const nextRequiredPkg = targetInfo ? (targetInfo.packages.findIndex((a) => !a) + 1) : 0;
  const canActivateThis = targetInfo && packageId === nextRequiredPkg;

  const doActivate = async () => {
    if (!ethers.utils.isAddress(wallet)) return notify.error("Invalid wallet address");
    if (!targetInfo) return notify.error("Look up the user first");
    if (!canActivateThis) return notify.error(`Must activate P${nextRequiredPkg} next (sequential)`);
    if (!hasBalance) return notify.error(`Not enough USDT for Package ${packageId}`);
    const logic = getLogic();
    const usdt = getUsdt();
    if (!logic || !usdt) return notify.error("Logic contract not configured");
    setBusy(true);
    try {
      if (allowance.lt(costBn)) {
        notify.info("Approving USDT...");
        const ax = await usdt.approve(CONTRACT_ADDRESS, ethers.constants.MaxUint256);
        await ax.wait();
        notify.success("Approved");
      }
      notify.info(`Activating Package ${packageId}...`);
      const tx = await logic.activatePackageFor(wallet, packageId);
      const receipt = await tx.wait();
      notify.success(`Package ${packageId} activated!`);
      // Record for active reward offers — tag against the TARGET wallet
      recordOfferEvent({
        wallet,
        action: "activate",
        packageId,
        amount: String(PACKAGES[packageId - 1]?.price || ""),
        txHash: receipt?.transactionHash || tx?.hash || "",
      });
      onDone();
    } catch (e) {
      notify.error(parseError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <div style={{ padding: 10, background: "#050815", border: "1px solid var(--border-strong)", borderRadius: 8, marginBottom: 14, fontSize: 11, color: "var(--text-muted)", lineHeight: 1.5 }}>
        Activate a <b style={{ color: "var(--cyan)" }}>single package</b> for any already-registered user. You pay, they receive the package. Packages must be activated sequentially (P1 → P2 → ... → P8).
      </div>

      <div className="input-group">
        <label className="input-label">Target User Wallet</label>
        <div style={{ display: "flex", gap: 8 }}>
          <input
            className="input"
            placeholder="0x..."
            value={wallet}
            onChange={(e) => { setWallet(e.target.value); setTargetInfo(null); }}
            disabled={busy}
          />
          <Button variant="secondary" size="sm" onClick={doLookup} loading={checking} disabled={busy}>
            Check
          </Button>
        </div>
      </div>

      {targetInfo && (
        <div style={{
          padding: 12,
          background: "#0a0e24",
          border: "1px solid var(--cyan-border)",
          borderRadius: 10,
          marginBottom: 14,
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: "var(--text-primary)", marginBottom: 4 }}>
            {targetInfo.name || "—"}
            <span style={{ marginLeft: 8, fontFamily: "var(--font-mono)", fontSize: 11, color: "var(--cyan)" }}>
              #{targetInfo.id}
            </span>
          </div>
          {targetInfo.sponsorId && (
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <span style={{ color: "var(--text-muted)" }}>Sponsor:</span>
              <span style={{ fontWeight: 600, color: "var(--text-primary)" }}>
                {targetInfo.sponsorName || "Unnamed"}
              </span>
              <span style={{ color: "var(--cyan)", fontFamily: "var(--font-mono)" }}>
                #{targetInfo.sponsorId}
              </span>
            </div>
          )}
          <div style={{ fontSize: 10, color: "var(--text-muted)", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 600 }}>
            Current Packages
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(8, 1fr)", gap: 3 }}>
            {PACKAGES.map((p, i) => (
              <div
                key={p.id}
                style={{
                  padding: "3px 0",
                  textAlign: "center",
                  fontSize: 10,
                  fontFamily: "var(--font-display)",
                  fontWeight: 700,
                  borderRadius: 4,
                  background: targetInfo.packages[i] ? "var(--cyan)" : "rgba(139,155,209,0.08)",
                  color: targetInfo.packages[i] ? "#000" : "var(--text-muted)",
                  border: targetInfo.packages[i] ? "none" : "1px solid var(--border)",
                }}
              >
                P{p.id}
              </div>
            ))}
          </div>
          {nextRequiredPkg > 0 && nextRequiredPkg <= 8 ? (
            <div style={{ fontSize: 11, color: "var(--text-secondary)", marginTop: 8 }}>
              Next allowed: <b style={{ color: "var(--cyan)" }}>Package {nextRequiredPkg}</b>
            </div>
          ) : (
            <div style={{ fontSize: 11, color: "var(--success)", marginTop: 8 }}>
              All 8 packages already active
            </div>
          )}
        </div>
      )}

      <div className="input-group">
        <label className="input-label">Package to Activate</label>
        <select className="select" value={packageId} onChange={(e) => setPackageId(Number(e.target.value))} disabled={busy}>
          {PACKAGES.map((p) => (
            <option key={p.id} value={p.id}>Package {p.id} — ${p.price}</option>
          ))}
        </select>
      </div>

      <div className="stat-grid mb-lg">
        <div className="stat-card">
          <div className="stat-card-label">Cost</div>
          <div className="stat-card-value" style={{ color: "var(--cyan)" }}>${cost}</div>
          <div className="stat-card-sub">Package {packageId}</div>
        </div>
        <div className="stat-card">
          <div className="stat-card-label">Your Balance</div>
          <div className="stat-card-value" style={{ color: hasBalance ? "var(--success)" : "var(--error)" }}>
            ${formatUSDT(usdtBalance)}
          </div>
        </div>
      </div>

      <Button
        variant="primary"
        full
        loading={busy}
        disabled={!hasBalance || !wallet || !targetInfo || !canActivateThis}
        onClick={doActivate}
      >
        {targetInfo && !canActivateThis && nextRequiredPkg > 0
          ? `Must activate P${nextRequiredPkg} first`
          : `Activate Package ${packageId}`}
      </Button>
    </div>
  );
}

export default Home;
